import { ref, type Ref } from "vue";
import { defineStore } from "pinia";
import { CarbucksViewModel, type ICarbucksViewModel } from "..";
import { useCarbucksApi } from "@app/Carbucks";

/**
 * Cache is implemented to prevent multiple successive calls from components on a page load
 * from causing the view model to be reloaded from the server.  The cache is kept short so that
 * the view model is reloaded if the user makes changes or is navigating between pages and we 
 * want the view model to be reloaded
 */
const useStore = defineStore( 'carbucks-view-model-store', () =>
{
    
    const CACHE_TIMEOUT_IN_SECONDS = 2;
    const instance: Ref<ICarbucksViewModel|undefined> = ref<ICarbucksViewModel>();
    
    var lastLoaded: Date = new Date();

    /**
     * True if the view model hasnt been loaded yet, or if its older than the cache timeout
     * @returns 
     */
    function needsRefresh(): boolean
    {
        if( instance.value == null ) return true;
        const age = ( new Date().getTime() - lastLoaded.getTime() ) / 1000;
        return age >= CACHE_TIMEOUT_IN_SECONDS;
    }

    /**
     * Updates the date the view model was last refreshed 
     */
    function markRefreshed()
    {
        lastLoaded = new Date();
    }

    return { instance, needsRefresh, markRefreshed };

} );

/**
 * Gets a new instance of the view model from the DTO returned from the API
 * @returns 
 */
async function getViewModelInstance()
{
    const api = useCarbucksApi();
    const dto = await api.getCarbucksProfileAsync();
    return new CarbucksViewModel( dto! );
}

/**
 * Gets the current view model instance from the store
 * @returns 
 */
export async function useViewModelInstance()
{
    const store = useStore();
    if( store.needsRefresh() ) 
    {
        store.instance = await getViewModelInstance();
        store.markRefreshed();
    }
    return store.instance as ICarbucksViewModel;
};
